import { alpha, Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import type { VisitTranscriptionActivity } from 'src/types/visit-transcription';

const breathe = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.12); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;
const flow = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.7); }
  50% { opacity: 1; transform: scale(1); }
`;

const COPY = {
  starting: {
    title: 'visit.transcriptionActivityStarting',
    detail: 'visit.transcriptionActivityStartingDetail',
    icon: 'solar:microphone-line-duotone',
  },
  recording: {
    title: 'visit.transcriptionActivityRecording',
    detail: 'visit.transcriptionActivityRecordingDetail',
    icon: 'solar:microphone-bold',
  },
  'recording-elsewhere': {
    title: 'visit.transcriptionActivityRecordingElsewhere',
    detail: 'visit.transcriptionActivityRecordingElsewhereDetail',
    icon: 'solar:microphone-line-duotone',
  },
  synchronizing: {
    title: 'visit.transcriptionActivitySynchronizing',
    detail: 'visit.transcriptionActivitySynchronizingDetail',
    icon: 'solar:document-text-bold-duotone',
  },
  reviewing: {
    title: 'visit.transcriptionActivityReviewing',
    detail: 'visit.transcriptionActivityReviewingDetail',
    icon: 'solar:magic-stick-3-bold-duotone',
  },
  stopping: {
    title: 'visit.transcriptionActivityStopping',
    detail: 'visit.transcriptionActivityStoppingDetail',
    icon: 'solar:document-text-bold-duotone',
  },
};

interface VisitTranscriptionActivityIndicatorProps {
  activity: VisitTranscriptionActivity;
  expanded?: boolean;
}

export default function VisitTranscriptionActivityIndicator({
  activity,
  expanded = false,
}: VisitTranscriptionActivityIndicatorProps): JSX.Element | null {
  const { t } = useTranslation();
  if (activity === 'idle') return null;
  const copy = COPY[activity];
  const recording = activity === 'recording';

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-transcription-activity={activity}
      sx={(theme) => ({
        flexShrink: 0,
        flexDirection: expanded ? 'column' : 'row',
        alignItems: 'center',
        gap: expanded ? 2.5 : 1.5,
        p: expanded ? 4 : 2,
        my: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.2),
        background: `linear-gradient(120deg, ${alpha(
          theme.palette.primary.main,
          0.09,
        )}, ${theme.palette.background.default})`,
        bgcolor: 'background.default',
        textAlign: expanded ? 'center' : 'left',
        '@media (prefers-reduced-motion: reduce)': {
          '& *, & *::before, & *::after': { animation: 'none !important' },
        },
      })}
    >
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          width: theme.spacing(expanded ? 8 : 5),
          height: theme.spacing(expanded ? 8 : 5),
          borderRadius: '50%',
          color: 'text.primary',
          bgcolor: alpha(theme.palette.primary.main, 0.16),
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: theme.spacing(-0.5),
            borderRadius: 'inherit',
            border: '1px solid',
            borderColor: 'primary.main',
            animation: `${breathe} 2.8s ease-in-out infinite`,
          },
        })}
      >
        <Iconify icon={copy.icon} sx={{ width: '50%', height: '50%' }} />
      </Box>
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography variant="subtitle2">{t(copy.title)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t(copy.detail)}
        </Typography>
      </Stack>
      <Stack
        aria-hidden="true"
        direction="row"
        sx={{ gap: 0.5, alignItems: 'center', flexShrink: 0 }}
      >
        {(recording ? [0] : [0, 1, 2]).map((index) => (
          <Box
            key={index}
            sx={(theme) => ({
              width: theme.spacing(recording ? 1 : 0.75),
              height: theme.spacing(recording ? 1 : 0.75),
              borderRadius: '50%',
              bgcolor: recording ? 'error.main' : 'primary.dark',
              animation: `${recording ? pulse : flow} ${
                recording ? '2.4s' : '1.4s'
              } ease-in-out infinite`,
              animationDelay: `${index * 0.16}s`,
            })}
          />
        ))}
      </Stack>
    </Stack>
  );
}
